import { useLocalization } from '~/framework';
import { useQuizSessionsContext } from '../QuizSessionsContext';
import {
   Box,
   Flex,
   Text,
   Modal,
   Stack,
   Button,
   useToast,
   ModalBody,
   ModalHeader,
   ModalFooter,
   ModalOverlay,
   ModalContent,
   ModalCloseButton,
   Alert,
   AlertIcon,
   AlertTitle,
   AlertDescription,
   CloseButton,
} from '@chakra-ui/react';
import { useFormatAbsoluteDateTime } from '~/ui-components';
import { useState } from 'react';

export function CancelModal() {
   const toast = useToast();
   const { _js } = useLocalization();
   const {
      selection,
      quizSessions,
      showCancelModal,
      setShowCancelModal,
      cancelAllQuizSessions,
      bulkCancelQuizSession,
      setCancelAllQuizSessions,
   } = useQuizSessionsContext();

   const formatter = useFormatAbsoluteDateTime();
   const [errorMessages, setErrorMessages] = useState<string[]>([]);

   const handleBulkCancelQuizSession = () => {
      setErrorMessages([]);
      bulkCancelQuizSession.mutate(
         {
            quiz_sessionids: selection.selected.map(session => session.quiz_sessionid),
         },
         {
            onSuccess: res => {
               toast.closeAll();
               if (res.cancelled_quiz_sessionids.length > 0) {
                  toast({
                     title: _js(
                        'Successfully cancelled %1 quiz sessions.',
                        res.cancelled_quiz_sessionids.length.toString()
                     ),
                     status: 'success',
                     position: 'top',
                     duration: 5000,
                     isClosable: true,
                  });
               }
               if (res.invalid_quiz_sessionids.length > 0) {
                  let errors = res.invalid_quiz_sessionids.map(error => {
                     const quizSession = quizSessions.find(qs => qs.quiz_sessionid === error.id);
                     let reason = '';
                     if (error.reason === 'no_privileges') reason = 'Invalid privileges.';
                     if (error.reason === 'cancelled') reason = 'Already cancelled.';
                     if (quizSession) {
                        const { username, quiz_title, last_modified } = quizSession;
                        const print_quiz_title =
                           quiz_title.length > 32
                              ? `${quiz_title.substring(0, 32)}...`
                              : quiz_title;
                        return _js(
                           '%1 "%2" taken on %3 - %4',
                           username,
                           print_quiz_title,
                           formatter(last_modified),
                           reason
                        );
                     }
                     return _js('Quiz Session #%1 does not exist.', `${error.id}`);
                  });

                  setErrorMessages(errors);
               }
               selection.clear();
               setShowCancelModal(false);
               setCancelAllQuizSessions(false);
            },
            onError: () => {
               toast.closeAll();
               toast({
                  title: _js('Unable to cancel quiz sessions'),
                  status: 'error',
                  position: 'top',
                  duration: 2000,
                  isClosable: true,
               });
            },
         }
      );
   };

   return (
      <>
         {(cancelAllQuizSessions || selection?.selected.length > 0) && (
            <Stack direction="row" spacing="2" pt="4">
               <Flex align="center">
                  <Text color="#6E7881">
                     {_js(
                        '%1 of %2 selected',
                        selection.selected.length.toString(),
                        quizSessions.length.toString()
                     )}
                  </Text>
               </Flex>
               <Flex
                  align="center"
                  borderRadius="2xl"
                  border="1px"
                  gap="6"
                  borderColor="#D2D5DA"
                  cursor="pointer"
                  backgroundColor="#FFF"
                  onClick={() => setShowCancelModal(!showCancelModal)}
               >
                  <Text py="2" px="4" color="#1F2937">
                     {_js('Cancel quiz sessions')}
                  </Text>
               </Flex>
            </Stack>
         )}

         {errorMessages.length > 0 && (
            <div>
               <Alert status="error" flexDirection="column">
                  <CloseButton
                     alignSelf="flex-start"
                     position="absolute"
                     right={1}
                     top={1}
                     onClick={() => setErrorMessages([])}
                  />
                  <AlertIcon />
                  <AlertTitle>
                     {_js('The following quiz sessions were unable to be cancelled:')}
                  </AlertTitle>
                  <AlertDescription>
                     <ul>
                        {errorMessages.map(error => (
                           <li key={error}>{error}</li>
                        ))}
                     </ul>
                  </AlertDescription>
               </Alert>
            </div>
         )}

         <Modal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)}>
            <ModalOverlay />
            <ModalContent>
               <ModalHeader>{_js('Cancel quiz sessions')}</ModalHeader>
               <ModalCloseButton />
               <ModalBody border="1px solid lightgray" padding="20px">
                  <Box padding="10px" display="inline-block">
                     <Text
                        dangerouslySetInnerHTML={{
                           __html: _js('This action %1cannot%2 be undone', '<b>', '</b>'),
                        }}
                     />
                     <Text
                        dangerouslySetInnerHTML={{
                           __html: _js(
                              'Cancelling the quiz will update its status to %1Cancelled%2. Are you sure you want to proceed?',
                              '<b>',
                              '</b>'
                           ),
                        }}
                     />
                  </Box>
               </ModalBody>
               <ModalFooter>
                  <Button
                     variant="outline"
                     colorScheme="gray"
                     onClick={() => setShowCancelModal(false)}
                     marginRight="20px"
                  >
                     {_js('Close')}
                  </Button>
                  <Button
                     onClick={handleBulkCancelQuizSession}
                     variant="outline"
                     colorScheme="white"
                     color="red"
                  >
                     {_js('Cancel quiz sessions')}
                  </Button>
               </ModalFooter>
            </ModalContent>
         </Modal>
      </>
   );
}
